import React from 'react';
import { cn } from '../lib/utils';

export const CloudIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 100 100" className={cn("drop-shadow-[0_4px_6px_rgba(0,0,0,0.2)]", className)}>
    <defs>
      <linearGradient id="cloudGrad" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="100%" stopColor="#e2e8f0" />
      </linearGradient>
    </defs>
    <path 
      d="M25 60 A15 15 0 1 1 35 30 A20 20 0 1 1 70 35 A15 15 0 1 1 75 60 Z" 
      fill="url(#cloudGrad)"
      stroke="#cbd5e1"
      strokeWidth="2"
    />
    <path d="M40 40 A5 5 0 0 1 45 35" stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
    <path d="M55 45 A4 4 0 0 1 59 41" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
  </svg>
);

export const ShellIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 100 100" className={cn("drop-shadow-[0_4px_4px_rgba(0,0,0,0.2)]", className)}>
    <defs>
      <linearGradient id="shellGradHome" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#38bdf8" />
        <stop offset="100%" stopColor="#0ea5e9" />
      </linearGradient>
    </defs>
    <path 
      d="M50 5 L85 25 L95 60 L75 90 L50 95 L25 90 L5 60 L15 25 Z" 
      fill="url(#shellGradHome)"
    />
    <path d="M50 5 L50 95" stroke="black" strokeWidth="1" strokeOpacity="0.05" fill="none" />
    <path d="M25 15 L35 92" stroke="black" strokeWidth="1" strokeOpacity="0.03" fill="none" />
    <path d="M75 15 L65 92" stroke="black" strokeWidth="1" strokeOpacity="0.03" fill="none" />
  </svg>
);

export const StarIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 100 100" className={cn("drop-shadow-[0_4px_6px_rgba(0,0,0,0.3)]", className)}>
    <defs>
      <linearGradient id="starGradHome" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fef08a" />
        <stop offset="100%" stopColor="#f59e0b" />
      </linearGradient>
      <filter id="starGlowHome" x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur stdDeviation="2" result="blur" />
        <feComposite in="SourceGraphic" in2="blur" operator="over" />
      </filter>
    </defs>

    {/* Base shadow layer */}
    <path 
      d="M 50 10 L 70 32 L 95 42 L 80 65 L 75 92 L 50 80 L 25 92 L 20 65 L 5 42 L 30 32 Z" 
      fill="rgba(217,119,6,0.4)"
      transform="translate(0, 4)"
    />

    {/* Main Base Body */}
    <path 
      d="M 50 10 L 70 32 L 95 42 L 80 65 L 75 92 L 50 80 L 25 92 L 20 65 L 5 42 L 30 32 Z" 
      fill="url(#starGradHome)"
      stroke="rgba(255,255,255,0.3)"
      strokeWidth="1"
    />

    {/* 3D Paper Folds */}
    <path d="M 50 52 L 30 32 L 50 10 Z" fill="white" fillOpacity="0.4" />
    <path d="M 50 52 L 5 42 L 30 32 Z" fill="white" fillOpacity="0.25" />
    
    <path d="M 50 52 L 50 10 L 70 32 Z" fill="white" fillOpacity="0.15" />
    <path d="M 50 52 L 70 32 L 95 42 Z" fill="white" fillOpacity="0.2" />

    <path d="M 50 52 L 95 42 L 80 65 Z" fill="black" fillOpacity="0.05" />
    <path d="M 50 52 L 80 65 L 75 92 Z" fill="black" fillOpacity="0.15" />
    <path d="M 50 52 L 75 92 L 50 80 Z" fill="black" fillOpacity="0.25" />

    <path d="M 50 52 L 50 80 L 25 92 Z" fill="black" fillOpacity="0.2" />
    <path d="M 50 52 L 25 92 L 20 65 Z" fill="black" fillOpacity="0.1" />
    <path d="M 50 52 L 20 65 L 5 42 Z" fill="black" fillOpacity="0.05" />

    {/* Center highlight for extra puffiness */}
    <circle cx="50" cy="52" r="10" fill="white" fillOpacity="0.2" filter="blur(3px)" />
  </svg>
);

export const GoldRushIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 100 100" className={cn("drop-shadow-lg", className)}>
    <defs>
      <linearGradient id="goldMountainGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fbbf24" />
        <stop offset="100%" stopColor="#d97706" />
      </linearGradient>
      <linearGradient id="shineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="100%" stopColor="#fbbf24" />
      </linearGradient>
    </defs>
    {/* Base Gold Pile */}
    <path d="M10 90 L50 20 L90 90 Z" fill="url(#goldMountainGrad)" />
    <path d="M5 90 L30 60 L55 90 Z" fill="#b45309" opacity="0.8" />
    {/* Shine Accent */}
    <path d="M40 50 L50 20 L60 50 L50 45 Z" fill="url(#shineGrad)" />
    {/* Nuggets */}
    <circle cx="20" cy="85" r="5" fill="#fcd34d" />
    <circle cx="80" cy="85" r="4" fill="#fcd34d" />
  </svg>
);
